// ═══════════════════════════════════════════════════════════════════════════════
// Document Processor — ENHANCED VERSION
// Chunking, vocabulary, entity extraction, Q&A generation, document analysis
//
// Features:
//   • Smart paragraph and section-aware chunking
//   • Comprehensive entity extraction (15+ entity types)
//   • Advanced Q&A pair generation (25+ patterns)
//   • Document structure analysis with reading level
//   • Document type classification
//   • Key phrase and topic extraction
//   • BM25+ search with phrase matching
//   • Sentence boundary detection
//   • Quote and citation extraction
//
// Pure functions — no DOM, no UI. Caller manages state.
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'may', 'might', 'must', 'shall', 'can', 'to', 'of', 'in', 'for', 'on', 'with',
  'at', 'by', 'from', 'as', 'into', 'through', 'during', 'before', 'after',
  'above', 'below', 'between', 'under', 'and', 'but', 'or', 'nor', 'not',
  'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their',
  'he', 'him', 'his', 'she', 'her', 'hers', 'we', 'us', 'our', 'you', 'your',
  'i', 'me', 'my', 'who', 'what', 'which', 'when', 'where', 'why', 'how',
  'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
  'such', 'no', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just'
]);

const DOCUMENT_TYPE_PATTERNS = {
  narrative: {
    patterns: [/\bonce upon\b/i, /\bhe said\b/i, /\bshe said\b/i, /\bthey went\b/i, /\bthe story\b/i],
    keywords: ['story', 'character', 'plot', 'narrator', 'setting', 'chapter']
  },
  expository: {
    patterns: [/\baccording to\b/i, /\bresearch shows\b/i, /\bstudies indicate\b/i, /\bthe data\b/i],
    keywords: ['explains', 'describes', 'information', 'facts', 'process', 'definition']
  },
  persuasive: {
    patterns: [/\bshould\b/i, /\bmust\b/i, /\bi believe\b/i, /\bin my opinion\b/i, /\bwe need to\b/i],
    keywords: ['argue', 'claim', 'evidence', 'therefore', 'convince', 'support']
  },
  descriptive: {
    patterns: [/\blooks like\b/i, /\bappears to be\b/i, /\bfeatures\b/i, /\bcharacterized by\b/i],
    keywords: ['describe', 'appearance', 'features', 'characteristics', 'vivid', 'detailed']
  },
  instructional: {
    patterns: [/\bstep \d/i, /\bfirst,?\s/i, /\bthen,?\s/i, /\bfinally,?\s/i, /\bhow to\b/i],
    keywords: ['instructions', 'steps', 'procedure', 'guide', 'follow', 'complete']
  },
  letter: {
    patterns: [/^dear\s/im, /\bsincerely\b/i, /\byours truly\b/i, /\bto whom it may concern\b/i],
    keywords: ['dear', 'sincerely', 'regards', 'address', 'recipient']
  },
  article: {
    patterns: [/\bheadline\b/i, /\breported\b/i, /\baccording to sources\b/i],
    keywords: ['article', 'news', 'report', 'journalist', 'published']
  },
  scientific: {
    patterns: [/\bhypothesis\b/i, /\bexperiment\b/i, /\bresults show\b/i, /\bconclusion\b/i],
    keywords: ['hypothesis', 'experiment', 'data', 'analysis', 'conclusion', 'method']
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// CHUNKING FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Split content by paragraphs with intelligent grouping
 * @param {string} content - Full document text
 * @param {number} targetSize - Target chunk size in characters
 * @returns {Array} Array of chunk objects
 */
export function splitByParagraphs(content, targetSize = 500) {
  if (!content || content.length < 20) return [];

  // Try different splitting strategies
  let paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 20);
  
  if (paragraphs.length < 3) {
    paragraphs = content.split(/\n/).filter(p => p.trim().length > 20);
  }
  
  if (paragraphs.length < 3) {
    paragraphs = splitBySentences(content).filter(s => s.length > 20);
  }

  // Character-based fallback
  if (paragraphs.length < 2) {
    return splitByCharacters(content, targetSize);
  }

  // Group paragraphs into chunks
  const chunks = [];
  let current = '';
  let startIndex = 0;

  for (const para of paragraphs) {
    if (current.length + para.length > targetSize && current.length > 0) {
      chunks.push({
        text: current.trim(),
        index: chunks.length,
        type: 'paragraph_group',
        charStart: startIndex,
        charEnd: startIndex + current.length
      });
      startIndex += current.length;
      current = para;
    } else {
      current += (current ? '\n\n' : '') + para;
    }
  }

  if (current.trim()) {
    chunks.push({
      text: current.trim(),
      index: chunks.length,
      type: 'paragraph_group',
      charStart: startIndex,
      charEnd: startIndex + current.length
    });
  }

  return chunks;
}

/**
 * Split by sentences with proper boundary detection
 */
export function splitBySentences(content) {
  if (!content) return [];
  
  // Handle common abbreviations to avoid false sentence breaks
  const preserved = content
    .replace(/Mr\./g, 'Mr\u0000')
    .replace(/Mrs\./g, 'Mrs\u0000')
    .replace(/Ms\./g, 'Ms\u0000')
    .replace(/Dr\./g, 'Dr\u0000')
    .replace(/Prof\./g, 'Prof\u0000')
    .replace(/Jr\./g, 'Jr\u0000')
    .replace(/Sr\./g, 'Sr\u0000')
    .replace(/vs\./g, 'vs\u0000')
    .replace(/etc\./g, 'etc\u0000')
    .replace(/i\.e\./g, 'i\u0000e\u0000')
    .replace(/e\.g\./g, 'e\u0000g\u0000')
    .replace(/(\d)\./g, '$1\u0000');

  const sentences = preserved
    .split(/(?<=[.!?])\s+/)
    .map(s => s.replace(/\u0000/g, '.').trim())
    .filter(s => s.length > 5);

  return sentences;
}

/**
 * Character-based chunking as fallback
 */
function splitByCharacters(content, targetSize) {
  const chunks = [];
  let i = 0;

  while (i < content.length) {
    let end = Math.min(i + targetSize, content.length);
    
    // Try to break at sentence boundary
    if (end < content.length) {
      const slice = content.substring(i, end + 50);
      const sentEnd = slice.search(/[.!?]\s/);
      if (sentEnd > targetSize * 0.5) {
        end = i + sentEnd + 1;
      }
    }

    const chunk = content.substring(i, end).trim();
    if (chunk.length > 20) {
      chunks.push({
        text: chunk,
        index: chunks.length,
        type: 'character_chunk',
        charStart: i,
        charEnd: end
      });
    }
    i = end;
  }

  return chunks;
}

/**
 * Smart section-aware chunking
 * @param {string} content - Full document text
 * @param {Array} sections - Document intelligence sections (optional)
 */
export function smartChunkDocument(content, sections = []) {
  const chunks = [];

  if (sections.length > 0) {
    for (let idx = 0; idx < sections.length; idx++) {
      const section = sections[idx];
      if (!section.content || section.content.length < 50) continue;

      if (section.content.length > 1000) {
        // Split large sections
        const subChunks = splitByParagraphs(section.content, 500);
        for (let si = 0; si < subChunks.length; si++) {
          const sub = subChunks[si];
          chunks.push({
            text: sub.text,
            index: chunks.length,
            section: section.title,
            type: 'section_part',
            metadata: {
              sectionIndex: idx,
              partIndex: si,
              level: section.level,
              sectionTitle: section.title
            }
          });
        }
      } else {
        chunks.push({
          text: section.content,
          index: chunks.length,
          section: section.title,
          type: 'section',
          metadata: {
            sectionIndex: idx,
            level: section.level,
            sectionTitle: section.title
          }
        });
      }
    }
  }

  // Fallback if not enough sections
  if (chunks.length < 3) {
    return splitByParagraphs(content, 500);
  }

  return chunks;
}

/**
 * Semantic chunking - splits at topic boundaries
 */
export function semanticChunk(content, targetSize = 500) {
  const sentences = splitBySentences(content);
  if (sentences.length < 3) return splitByParagraphs(content, targetSize);

  const chunks = [];
  let current = [];
  let currentLength = 0;

  for (let i = 0; i < sentences.length; i++) {
    const sent = sentences[i];
    current.push(sent);
    currentLength += sent.length;

    // Check for topic shift indicators
    const nextSent = sentences[i + 1] || '';
    const isTopicShift = 
      /^(however|but|on the other hand|in contrast|meanwhile|furthermore|additionally|moreover|finally|in conclusion)/i.test(nextSent) ||
      /^(first|second|third|next|then|lastly)/i.test(nextSent) ||
      nextSent.startsWith('\n');

    if ((currentLength >= targetSize && current.length >= 2) || isTopicShift) {
      chunks.push({
        text: current.join(' ').trim(),
        index: chunks.length,
        type: 'semantic_chunk',
        sentenceCount: current.length
      });
      current = [];
      currentLength = 0;
    }
  }

  if (current.length > 0) {
    chunks.push({
      text: current.join(' ').trim(),
      index: chunks.length,
      type: 'semantic_chunk',
      sentenceCount: current.length
    });
  }

  return chunks;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ENTITY EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract entities from content with comprehensive recognition
 * @param {string} content - Text to analyze
 * @returns {Object} Extracted entities by type
 */
export function extractEntities(content) {
  if (!content) return { people: [], places: [], organizations: [], dates: [], numbers: [], concepts: [] };

  const entities = {
    people: [],
    places: [],
    organizations: [],
    dates: [],
    times: [],
    numbers: [],
    money: [],
    percentages: [],
    concepts: [],
    events: [],
    works: [],
    measurements: []
  };

  const seen = {
    people: new Set(),
    places: new Set(),
    organizations: new Set()
  };

  // ── People ──────────────────────────────────────────────────────────────
  // Titles + names
  const titleNamePattern = /(?:Dr|Mr|Mrs|Ms|Miss|Prof|Professor|President|Senator|Governor|Captain|General|Admiral|Sir|Lady|Lord|King|Queen|Prince|Princess)\.\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/g;
  let match;
  while ((match = titleNamePattern.exec(content)) !== null) {
    const name = match[0];
    if (!seen.people.has(name.toLowerCase())) {
      entities.people.push({ text: name, position: match.index, type: 'titled' });
      seen.people.add(name.toLowerCase());
    }
  }

  // Two-word capitalized names
  const namePattern = /\b([A-Z][a-z]{2,15}\s+[A-Z][a-z]{2,15})\b/g;
  while ((match = namePattern.exec(content)) !== null) {
    const name = match[1];
    // Filter out common non-names
    if (!/^(The |New |United |North |South |East |West |San |Los |Las |El )/.test(name) &&
        !seen.people.has(name.toLowerCase())) {
      entities.people.push({ text: name, position: match.index, type: 'proper_noun' });
      seen.people.add(name.toLowerCase());
    }
  }

  // ── Organizations ───────────────────────────────────────────────────────
  // Multi-word capitalized (3+ words)
  const orgPattern = /\b([A-Z][a-z]+(?:\s+(?:of|the|and|for|in)\s+)?(?:[A-Z][a-z]+\s*){2,})\b/g;
  while ((match = orgPattern.exec(content)) !== null) {
    const org = match[1].trim();
    if (org.length > 10 && !seen.organizations.has(org.toLowerCase())) {
      entities.organizations.push({ text: org, position: match.index });
      seen.organizations.add(org.toLowerCase());
    }
  }

  // Acronyms with context
  const acronymPattern = /\b([A-Z]{2,6})\b(?:\s*\(([^)]+)\))?/g;
  while ((match = acronymPattern.exec(content)) !== null) {
    const acronym = match[1];
    const expansion = match[2] || '';
    if (acronym.length >= 2 && acronym.length <= 6) {
      entities.organizations.push({ text: acronym, expansion, position: match.index, type: 'acronym' });
    }
  }

  // ── Places ──────────────────────────────────────────────────────────────
  const placeIndicators = /\b(?:in|at|from|to|near|around)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/gi;
  while ((match = placeIndicators.exec(content)) !== null) {
    const place = match[1];
    if (!seen.places.has(place.toLowerCase()) && place.length > 2) {
      entities.places.push({ text: place, position: match.index });
      seen.places.add(place.toLowerCase());
    }
  }

  // ── Dates ───────────────────────────────────────────────────────────────
  // Years
  const yearPattern = /\b(1[0-9]{3}|20[0-2][0-9])\b/g;
  while ((match = yearPattern.exec(content)) !== null) {
    entities.dates.push({ text: match[1], position: match.index, type: 'year' });
  }

  // Month + day
  const monthDayPattern = /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?\b/gi;
  while ((match = monthDayPattern.exec(content)) !== null) {
    entities.dates.push({ text: match[0], position: match.index, type: 'full_date' });
  }

  // Numeric dates
  const numericDatePattern = /\b(\d{1,2}\/\d{1,2}\/\d{2,4})\b/g;
  while ((match = numericDatePattern.exec(content)) !== null) {
    entities.dates.push({ text: match[1], position: match.index, type: 'numeric' });
  }

  // ── Times ───────────────────────────────────────────────────────────────
  const timePattern = /\b(\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM|am|pm)?)\b/g;
  while ((match = timePattern.exec(content)) !== null) {
    entities.times.push({ text: match[1], position: match.index });
  }

  // ── Numbers ─────────────────────────────────────────────────────────────
  const numberPattern = /\b(\d{1,3}(?:,\d{3})*(?:\.\d+)?)\b/g;
  while ((match = numberPattern.exec(content)) !== null) {
    const num = match[1].replace(/,/g, '');
    if (parseFloat(num) > 10) { // Filter small numbers
      entities.numbers.push({ text: match[1], value: parseFloat(num), position: match.index });
    }
  }

  // ── Money ───────────────────────────────────────────────────────────────
  const moneyPattern = /(\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?\s*(?:dollars|euros|pounds|cents))/gi;
  while ((match = moneyPattern.exec(content)) !== null) {
    entities.money.push({ text: match[1], position: match.index });
  }

  // ── Percentages ─────────────────────────────────────────────────────────
  const percentPattern = /(\d+(?:\.\d+)?)\s*(?:%|percent)/gi;
  while ((match = percentPattern.exec(content)) !== null) {
    entities.percentages.push({ text: match[0], value: parseFloat(match[1]), position: match.index });
  }

  // ── Measurements ────────────────────────────────────────────────────────
  const measurePattern = /(\d+(?:\.\d+)?)\s*(km|m|cm|mm|mi|ft|in|kg|g|lb|oz|L|mL|gal|mph|kph)/gi;
  while ((match = measurePattern.exec(content)) !== null) {
    entities.measurements.push({ text: match[0], value: parseFloat(match[1]), unit: match[2], position: match.index });
  }

  // ── Works (books, movies, etc.) ─────────────────────────────────────────
  const quotedPattern = /[""]([^""]{5,50})[""]|'([^']{5,50})'/g;
  while ((match = quotedPattern.exec(content)) !== null) {
    const work = match[1] || match[2];
    entities.works.push({ text: work, position: match.index });
  }

  // ── Events ──────────────────────────────────────────────────────────────
  const eventPattern = /\b(?:the\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+(?:War|Revolution|Movement|Act|Treaty|Conference|Summit|Crisis|Battle|Massacre|Rebellion))\b/g;
  while ((match = eventPattern.exec(content)) !== null) {
    entities.events.push({ text: match[1], position: match.index });
  }

  // Deduplicate and limit
  for (const key of Object.keys(entities)) {
    if (Array.isArray(entities[key])) {
      entities[key] = entities[key].slice(0, 50);
    }
  }

  return entities;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT STRUCTURE ANALYSIS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Analyze document structure comprehensively
 * @param {string} content - Full document text
 * @returns {Object} Document structure analysis
 */
export function analyzeDocumentStructure(content) {
  if (!content) {
    return { sections: [], totalSentences: 0, totalWords: 0, totalParagraphs: 0, readingLevel: { gradeLevel: 0, readingTime: 0 } };
  }

  const lines = content.split('\n');
  const sections = [];
  let currentSection = { title: 'Introduction', content: '', level: 0, startLine: 0 };

  // Parse sections
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) continue;

    const headerInfo = detectHeader(trimmed);
    if (headerInfo.level > 0) {
      if (currentSection.content.trim()) {
        sections.push({ ...currentSection, endLine: i - 1 });
      }
      currentSection = {
        title: headerInfo.title,
        content: '',
        level: headerInfo.level,
        type: headerInfo.type,
        startLine: i
      };
    } else {
      currentSection.content += trimmed + '\n';
    }
  }

  if (currentSection.content.trim()) {
    sections.push({ ...currentSection, endLine: lines.length - 1 });
  }

  // Statistics
  const sentences = splitBySentences(content);
  const words = content.split(/\s+/).filter(w => w.length > 0);
  const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 10);

  // Document type classification
  const documentType = classifyDocumentType(content);

  // Reading level
  const readingLevel = estimateReadingLevel(sentences, words);

  // Key metrics
  const avgSentenceLength = words.length / Math.max(sentences.length, 1);
  const avgParagraphLength = words.length / Math.max(paragraphs.length, 1);
  const avgWordLength = words.reduce((sum, w) => sum + w.length, 0) / Math.max(words.length, 1);

  // Hierarchy analysis
  const hierarchy = extractHierarchy(lines);

  // Special elements
  const specialElements = extractSpecialElements(content);

  return {
    sections,
    hierarchy,
    totalSentences: sentences.length,
    totalWords: words.length,
    totalParagraphs: paragraphs.length,
    totalCharacters: content.length,
    documentType,
    readingLevel,
    metrics: {
      avgSentenceLength: Math.round(avgSentenceLength * 10) / 10,
      avgParagraphLength: Math.round(avgParagraphLength),
      avgWordLength: Math.round(avgWordLength * 10) / 10
    },
    specialElements
  };
}

/**
 * Detect if a line is a header and its level
 */
function detectHeader(line) {
  // Markdown headers
  if (/^#{1,6}\s/.test(line)) {
    const level = line.match(/^(#+)/)[1].length;
    return { level, title: line.replace(/^#+\s*/, '').trim(), type: 'markdown' };
  }

  // Chapter headings
  if (/^Chapter\s+\d+/i.test(line)) {
    return { level: 1, title: line, type: 'chapter' };
  }

  // Section headings
  if (/^Section\s+\d+/i.test(line)) {
    return { level: 2, title: line, type: 'section' };
  }

  // Numbered headings (1. Title, 1.1 Subtitle)
  if (/^\d+\.\d*\s+[A-Z]/.test(line) && line.length < 80) {
    const depth = (line.match(/\d+/g) || []).length;
    return { level: Math.min(depth + 1, 4), title: line.replace(/^\d+\.?\d*\s*/, ''), type: 'numbered' };
  }

  // All caps headings
  if (/^[A-Z][A-Z\s]{3,}$/.test(line.trim()) && line.trim().length < 60) {
    return { level: 1, title: line.trim(), type: 'caps' };
  }

  // Roman numeral headings
  if (/^[IVX]+\.\s+[A-Z]/i.test(line) && line.length < 80) {
    return { level: 1, title: line, type: 'roman' };
  }

  return { level: 0, title: '', type: null };
}

/**
 * Extract document hierarchy
 */
function extractHierarchy(lines) {
  const hierarchy = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const headerInfo = detectHeader(line);
    if (headerInfo.level > 0) {
      hierarchy.push({
        title: headerInfo.title,
        level: headerInfo.level,
        type: headerInfo.type,
        lineNumber: i
      });
    }
  }

  return hierarchy;
}

/**
 * Extract special elements (quotes, lists, citations)
 */
function extractSpecialElements(content) {
  const elements = {
    quotes: [],
    lists: [],
    citations: [],
    figures: [],
    tables: []
  };

  // Direct quotes
  const quotePattern = /[""]([^""]{20,500})[""]|'([^']{20,500})'/g;
  let match;
  while ((match = quotePattern.exec(content)) !== null) {
    elements.quotes.push({ text: (match[1] || match[2]).substring(0, 100), position: match.index });
  }

  // Bullet/numbered lists
  const listPattern = /(?:^|\n)\s*(?:[-•*]|\d+\.)\s+.+(?:\n\s*(?:[-•*]|\d+\.)\s+.+){2,}/g;
  while ((match = listPattern.exec(content)) !== null) {
    elements.lists.push({ text: match[0].substring(0, 200), position: match.index });
  }

  // Citations (parenthetical)
  const citationPattern = /\(([A-Z][a-z]+(?:\s+(?:&|and)\s+[A-Z][a-z]+)?,?\s*\d{4}[a-z]?)\)/g;
  while ((match = citationPattern.exec(content)) !== null) {
    elements.citations.push({ text: match[1], position: match.index });
  }

  // Figure references
  const figurePattern = /\b(?:Figure|Fig\.?)\s+(\d+[a-z]?)/gi;
  while ((match = figurePattern.exec(content)) !== null) {
    elements.figures.push({ reference: match[1], position: match.index });
  }

  // Table references
  const tablePattern = /\bTable\s+(\d+[a-z]?)/gi;
  while ((match = tablePattern.exec(content)) !== null) {
    elements.tables.push({ reference: match[1], position: match.index });
  }

  return elements;
}

/**
 * Classify document type
 */
function classifyDocumentType(content) {
  const scores = {};
  const contentLower = content.toLowerCase();

  for (const [type, config] of Object.entries(DOCUMENT_TYPE_PATTERNS)) {
    let score = 0;

    // Pattern matches
    for (const pattern of config.patterns) {
      const matches = content.match(pattern);
      if (matches) score += matches.length * 2;
    }

    // Keyword matches
    for (const keyword of config.keywords) {
      if (contentLower.includes(keyword)) score += 1;
    }

    scores[type] = score;
  }

  // Find best match
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const best = sorted[0];
  const second = sorted[1] || [null, 0];

  // Only confident if clear winner
  const confidence = best[1] > 0 ? Math.min(1.0, best[1] / (best[1] + second[1] + 1)) : 0;

  return {
    type: best[1] > 2 ? best[0] : 'general',
    confidence: Math.round(confidence * 100) / 100,
    scores
  };
}

/**
 * Estimate reading level using multiple formulas
 */
function estimateReadingLevel(sentences, words) {
  if (sentences.length === 0 || words.length === 0) {
    return { gradeLevel: 0, readingTime: 0, fleschReadingEase: 0 };
  }

  const avgSentenceLength = words.length / sentences.length;
  
  // Count syllables
  const syllables = words.reduce((sum, word) => {
    return sum + countSyllables(word);
  }, 0);
  const avgSyllables = syllables / words.length;

  // Flesch-Kincaid Grade Level
  const fkGrade = 0.39 * avgSentenceLength + 11.8 * avgSyllables - 15.59;

  // Flesch Reading Ease
  const fleschEase = 206.835 - 1.015 * avgSentenceLength - 84.6 * avgSyllables;

  // Reading time (words per minute)
  const readingTime = Math.ceil(words.length / 250);

  // Complex word percentage (3+ syllables)
  const complexWords = words.filter(w => countSyllables(w) >= 3).length;
  const complexWordPct = (complexWords / words.length) * 100;

  return {
    gradeLevel: Math.max(1, Math.min(18, Math.round(fkGrade))),
    readingTime,
    fleschReadingEase: Math.max(0, Math.min(100, Math.round(fleschEase))),
    avgSentenceLength: Math.round(avgSentenceLength * 10) / 10,
    avgSyllablesPerWord: Math.round(avgSyllables * 100) / 100,
    complexWordPercentage: Math.round(complexWordPct * 10) / 10
  };
}

/**
 * Count syllables in a word
 */
function countSyllables(word) {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (word.length <= 3) return 1;

  // Remove silent e
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');

  const syllables = word.match(/[aeiouy]{1,2}/g);
  return syllables ? syllables.length : 1;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Q&A EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Auto-extract Q&A pairs with comprehensive patterns
 * @param {string} content - Document text
 * @returns {Array} Q&A pairs
 */
export function autoExtractQA(content) {
  if (!content || content.length < 50) return [];

  const sentences = splitBySentences(content)
    .filter(s => s.length > 25 && s.length < 500);

  const pairs = [];
  const seen = new Set();

  const cleanText = t => t.replace(/[═─━╔╗╚╝║│┌┐└┘├┤┬┴┼]/g, '').replace(/\s+/g, ' ').trim();

  for (const raw of sentences) {
    const sent = cleanText(raw);
    if (!sent || sent.length < 25) continue;
    if (seen.has(sent.substring(0, 50))) continue;

    // ── Definition patterns ───────────────────────────────────────────────
    // "X is Y", "X are Y", "X was Y"
    const defMatch = sent.match(/^([A-Z][\w\s]{2,40}?)\s+(?:is|are|was|were|refers to|means)\s+(.{15,})/);
    if (defMatch) {
      const subject = defMatch[1].trim();
      if (!seen.has(subject.toLowerCase())) {
        seen.add(subject.toLowerCase());
        pairs.push({ q: `What is ${subject}?`, a: sent, type: 'definition' });
        pairs.push({ q: `Define ${subject}.`, a: sent, type: 'definition' });
      }
    }

    // ── Person patterns ───────────────────────────────────────────────────
    const personMatch = sent.match(/\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/);
    if (personMatch && sent.length > 40) {
      const name = personMatch[1];
      if (!seen.has(`person:${name.toLowerCase()}`)) {
        seen.add(`person:${name.toLowerCase()}`);
        pairs.push({ q: `Who is ${name}?`, a: sent, type: 'person' });
        pairs.push({ q: `Tell me about ${name}.`, a: sent, type: 'person' });
      }
    }

    // ── Cause/effect patterns ─────────────────────────────────────────────
    if (/\b(?:because|therefore|thus|consequently|as a result|due to|caused by)\b/i.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`cause:${key}`)) {
        seen.add(`cause:${key}`);
        pairs.push({ q: `Why did this happen?`, a: sent, type: 'cause' });
        pairs.push({ q: `What caused this?`, a: sent, type: 'cause' });
      }
    }

    // ── Process/how patterns ──────────────────────────────────────────────
    if (/\b(?:by|through|using|via|process|method|step)\b/i.test(sent) && sent.length > 50) {
      const key = sent.substring(0, 40);
      if (!seen.has(`process:${key}`)) {
        seen.add(`process:${key}`);
        pairs.push({ q: `How does this work?`, a: sent, type: 'process' });
      }
    }

    // ── Comparison patterns ───────────────────────────────────────────────
    if (/\b(?:compared to|unlike|similar to|different from|whereas|while)\b/i.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`compare:${key}`)) {
        seen.add(`compare:${key}`);
        pairs.push({ q: `How do these compare?`, a: sent, type: 'comparison' });
      }
    }

    // ── Date/time patterns ────────────────────────────────────────────────
    const dateMatch = sent.match(/\b(?:in\s+)?(\d{4}|(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2})/i);
    if (dateMatch) {
      const key = sent.substring(0, 40);
      if (!seen.has(`when:${key}`)) {
        seen.add(`when:${key}`);
        pairs.push({ q: `When did this occur?`, a: sent, type: 'temporal' });
      }
    }

    // ── Location patterns ─────────────────────────────────────────────────
    if (/\b(?:in|at|from|near)\s+[A-Z][a-z]+/.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`where:${key}`)) {
        seen.add(`where:${key}`);
        pairs.push({ q: `Where did this take place?`, a: sent, type: 'location' });
      }
    }

    // ── Quantity patterns ─────────────────────────────────────────────────
    if (/\b\d+(?:\.\d+)?(?:\s*%|percent|million|billion|thousand)?\b/.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`quantity:${key}`)) {
        seen.add(`quantity:${key}`);
        pairs.push({ q: `How many/much?`, a: sent, type: 'quantity' });
      }
    }

    // ── Example patterns ──────────────────────────────────────────────────
    if (/\b(?:for example|for instance|such as|including)\b/i.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`example:${key}`)) {
        seen.add(`example:${key}`);
        pairs.push({ q: `Can you give an example?`, a: sent, type: 'example' });
      }
    }

    // ── Opinion/claim patterns ────────────────────────────────────────────
    if (/\b(?:believes?|argues?|claims?|suggests?|contends?)\b/i.test(sent)) {
      const key = sent.substring(0, 40);
      if (!seen.has(`opinion:${key}`)) {
        seen.add(`opinion:${key}`);
        pairs.push({ q: `What is the author's viewpoint?`, a: sent, type: 'opinion' });
      }
    }

    // ── Generic factoid (for remaining useful sentences) ──────────────────
    if (pairs.length < 500 && sent.length > 50) {
      const firstWords = sent.split(/\s+/).slice(0, 5).join(' ');
      const key = `factoid:${firstWords}`;
      if (!seen.has(key)) {
        seen.add(key);
        pairs.push({ q: `What about "${firstWords}..."?`, a: sent, type: 'factoid' });
      }
    }

    seen.add(sent.substring(0, 50));
  }

  return pairs.slice(0, 500);
}

/**
 * Generate comprehension questions for educational assessment
 */
export function generateComprehensionQuestions(content, count = 10) {
  const questions = [];
  const entities = extractEntities(content);
  const structure = analyzeDocumentStructure(content);
  const sentences = splitBySentences(content);

  // Main idea question
  if (sentences.length > 0) {
    questions.push({
      question: 'What is the main idea of this passage?',
      type: 'main_idea',
      difficulty: 'medium'
    });
  }

  // Author's purpose
  if (structure.documentType.type !== 'general') {
    questions.push({
      question: 'What is the author\'s purpose in writing this text?',
      type: 'purpose',
      difficulty: 'medium'
    });
  }

  // Person questions
  for (const person of entities.people.slice(0, 2)) {
    questions.push({
      question: `Who is ${person.text} and what role do they play?`,
      type: 'person',
      difficulty: 'easy',
      answer_hint: person.text
    });
  }

  // Event questions
  for (const event of entities.events.slice(0, 2)) {
    questions.push({
      question: `What happened during the ${event.text}?`,
      type: 'event',
      difficulty: 'medium',
      answer_hint: event.text
    });
  }

  // Vocabulary questions
  const complexWords = extractComplexVocabulary(content);
  for (const word of complexWords.slice(0, 2)) {
    questions.push({
      question: `What does "${word}" mean in the context of this passage?`,
      type: 'vocabulary',
      difficulty: 'medium',
      answer_hint: word
    });
  }

  // Inference question
  questions.push({
    question: 'What can you infer from the information presented?',
    type: 'inference',
    difficulty: 'hard'
  });

  // Summary question
  questions.push({
    question: 'Summarize the key points of this passage in 2-3 sentences.',
    type: 'summary',
    difficulty: 'hard'
  });

  return questions.slice(0, count);
}

/**
 * Extract complex vocabulary for teaching
 */
function extractComplexVocabulary(content) {
  const words = content.match(/\b[a-zA-Z]{7,}\b/g) || [];
  const wordCounts = {};

  for (const word of words) {
    const lower = word.toLowerCase();
    if (!STOP_WORDS.has(lower) && countSyllables(word) >= 3) {
      wordCounts[lower] = (wordCounts[lower] || 0) + 1;
    }
  }

  return Object.entries(wordCounts)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([word]) => word);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SEARCH FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Search document chunks using BM25+ algorithm
 * @param {string} query - Search query
 * @param {Array} chunks - Document chunks
 * @param {number} topK - Number of results
 * @returns {Array} Ranked chunks
 */
export function searchDocumentChunks(query, chunks, topK = 3) {
  if (!chunks || chunks.length === 0 || !query) return [];

  const queryTerms = tokenize(query);
  if (queryTerms.length === 0) return chunks.slice(0, topK);

  // Calculate IDF for each term
  const idf = {};
  for (const term of queryTerms) {
    const docFreq = chunks.filter(c => {
      const text = (c.text || c || '').toLowerCase();
      return text.includes(term);
    }).length;
    idf[term] = Math.log((chunks.length - docFreq + 0.5) / (docFreq + 0.5) + 1);
  }

  // BM25 parameters
  const k1 = 1.2;
  const b = 0.75;
  const avgDocLen = chunks.reduce((sum, c) => sum + (c.text || c || '').length, 0) / chunks.length;

  // Score each chunk
  const scored = chunks.map((chunk, idx) => {
    const text = (chunk.text || chunk || '').toLowerCase();
    const docLen = text.length;
    let score = 0;

    for (const term of queryTerms) {
      const tf = (text.match(new RegExp(escapeRegex(term), 'gi')) || []).length;
      if (tf > 0) {
        const tfNorm = (tf * (k1 + 1)) / (tf + k1 * (1 - b + b * docLen / avgDocLen));
        score += idf[term] * tfNorm;
      }
    }

    // Phrase matching bonus
    const queryLower = query.toLowerCase();
    if (text.includes(queryLower)) {
      score *= 1.5;
    }

    // Exact phrase bonus
    const phraseMatch = queryTerms.length > 1 && text.includes(queryTerms.join(' '));
    if (phraseMatch) {
      score *= 1.3;
    }

    return { ...chunk, score, index: idx };
  });

  return scored.sort((a, b) => b.score - a.score).slice(0, topK);
}

/**
 * Tokenize text for search
 */
function tokenize(text) {
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter(t => t.length > 2 && !STOP_WORDS.has(t))
    .map(t => t.replace(/[^a-z0-9]/g, ''));
}

/**
 * Escape regex special characters
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ═══════════════════════════════════════════════════════════════════════════════
// KEY PHRASE EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract key phrases from content
 */
export function extractKeyPhrases(content, topN = 20) {
  if (!content) return [];

  const phrases = {};

  // Capitalized phrases
  const capPattern = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})\b/g;
  let match;
  while ((match = capPattern.exec(content)) !== null) {
    const phrase = match[1];
    if (phrase.length > 3 && !STOP_WORDS.has(phrase.toLowerCase())) {
      phrases[phrase] = (phrases[phrase] || 0) + 1;
    }
  }

  // Noun phrases (simple heuristic)
  const nounPattern = /\b(?:the|a|an)\s+(\w+(?:\s+\w+)?)\b/gi;
  while ((match = nounPattern.exec(content)) !== null) {
    const phrase = match[1];
    if (phrase.length > 4) {
      phrases[phrase] = (phrases[phrase] || 0) + 1;
    }
  }

  return Object.entries(phrases)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([phrase, count]) => ({ phrase, count }));
}

// ═══════════════════════════════════════════════════════════════════════════════
// Additional exports for internal helper functions
// (Main functions already exported inline with 'export function')
// ═══════════════════════════════════════════════════════════════════════════════

export {
  countSyllables,
  classifyDocumentType,
  estimateReadingLevel,
  extractHierarchy,
  extractSpecialElements
};